import { useQuery } from "@tanstack/react-query";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import { getLatestPulse } from "@app/analytics/Pulse/api";
import type { PulseResult } from "@app/analytics/Pulse/types";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorage from "@app/common/hooks/useLocalStorage";

export interface UseForYouTeaser {
  onDismissTeaser: (id: string) => void;
  result: PulseResult | null;
  shouldUpsell: boolean;
  onDismissUpsell: () => void;
}

const useForYouTeaser = (): UseForYouTeaser => {
  const { user } = useAuth();
  const { currentPlan } = useBillingContext();

  const [lastDismissedForYouTeaserId, setLastDismissedForYouTeaserId] =
    useLocalStorage<string | null>("lastDismissedForYouTeaserId", null);

  const [teaserUpsellDismissed, setTeaserUpsellDismissed] =
    useLocalStorage<boolean>("forYouTeaserUpsellDismissed", false);

  const latestForYouResultQuery = useQuery({
    queryKey: ["forYou", user?.id],
    queryFn: getLatestPulse,
  });

  const hasDismissedTeaserAlready =
    lastDismissedForYouTeaserId != null &&
    latestForYouResultQuery.data?.id === lastDismissedForYouTeaserId;

  const handleDismissTeaser = (id: string) =>
    setLastDismissedForYouTeaserId(id);

  const handleDismissUpsell = () => setTeaserUpsellDismissed(true);

  const result = latestForYouResultQuery?.data ?? null;

  return {
    onDismissTeaser: handleDismissTeaser,
    result: hasDismissedTeaserAlready ? null : result,
    shouldUpsell:
      !latestForYouResultQuery.isLoading &&
      !result &&
      !teaserUpsellDismissed &&
      isPaidPlan(currentPlan.type),
    onDismissUpsell: handleDismissUpsell,
  };
};

export default useForYouTeaser;
